import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import { variant } from 'styled-system';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Flex } from 'rebass/styled-components';
import { Text } from '@alefeducation/ui-elements-library-core/lib/Text';
import { Heading } from '@alefeducation/ui-elements-library-core/lib/Heading';
import PropTypes from 'prop-types';

export const SelectionBlock = forwardRef((props, ref) => {
  const { active, disabled, title, value, children, ...rest } = props;
  const activeClass = active ? 'active' : '';
  const disabledClass = disabled ? 'disabled' : '';

  return (
    <StyledSelectionBlock
      ref={ref}
      role="button"
      aria-disabled={disabled}
      aria-pressed={active}
      className={`${activeClass} ${disabledClass} selection-block`}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      data-testid={
        title
          ? `assignment-stats__${title.toLowerCase()}-progress`
          : 'assignment-stats'
      }
      {...rest}
    >
      {value && <SelectionBlockHeader>{value}</SelectionBlockHeader>}
      {title && <SelectionBlockSubHeader>{title}</SelectionBlockSubHeader>}
      {children}
    </StyledSelectionBlock>
  );
});

SelectionBlock.displayName = 'SelectionBlock';
SelectionBlock.defaultProps = {
  variant: 'default',
  size: 'lg',
  disabled: false,
  active: false,
  title: '',
  value: '',
  children: null,
};

export const SelectionBlockHeader = props => (
  <Heading
    variant="heading-small"
    weight="bold"
    color={props.color}
    className="selection-block__header"
  >
    {props.children}
  </Heading>
);
SelectionBlockHeader.propTypes = {
  color: PropTypes.string,
  children: PropTypes.node.isRequired,
};
SelectionBlockHeader.defaultProps = {
  color: '',
};

export const SelectionBlockSubHeader = props => (
  <Text
    variant="body-medium"
    className="selection-block__sub-header"
    weight="light"
    color=""
  >
    {props.children}
  </Text>
);
SelectionBlockSubHeader.propTypes = {
  children: PropTypes.node.isRequired,
};

const overrideColorForMobile = (desktop, mobile) => {
  if (window.innerWidth > 1400) {
    return desktop;
  }
  return mobile;
};

SelectionBlock.propTypes = {
  size: PropTypes.oneOf('sm', 'lg'),
  variant: PropTypes.oneOf(
    'default',
    'noBorder',
    'locked',
    'completed',
    'inProgress',
    'notStarted',
  ),
  disabled: PropTypes.bool,
  active: PropTypes.bool,
  title: PropTypes.string,
  value: PropTypes.number,
  children: PropTypes.node,
};

const defaultBoxStyle = {
  borderRadius: 'default',
  borderStyle: 'solid',
  borderWidth: '2px',
  fontWeight: 'bold',
};

const typeVariants = variant({
  variants: {
    default: {
      ...defaultBoxStyle,
      cursor: 'default',
      borderColor: '#21bcb6',
    },
    noBorder: {
      ...defaultBoxStyle,
      borderColor: '#ffffff',
      cursor: 'default',
      '&.disabled': {
        opacity: 0.4,
      },
    },
    locked: {
      ...defaultBoxStyle,
      borderColor: '#b6bbc1',
      '&:hover': {
        backgroundColor: overrideColorForMobile('#ecedef', 'transparent'),
      },
      '&:active': {
        bg: '#b6bbc1',
      },
      '&.active': {
        backgroundColor: '#ecedef',
        color: '#485465',
      },
      '&.disabled': {
        opacity: 0.4,
      },
    },
    completed: {
      ...defaultBoxStyle,
      borderColor: '#1a9692',
      backgroundColor: '#1a9692',
      color: '#ffffff',
      borderRadius: '4px',
    },
    inProgress: {
      ...defaultBoxStyle,
      backgroundColor: '#bceae9',
      color: '#0d4b49',
      borderRadius: '4px',
      borderWidth: '0px',
    },
    notStarted: {
      ...defaultBoxStyle,
      backgroundColor: '#bceae9',
      color: '#0d4b49',
      borderRadius: '4px',
      borderWidth: '0px',
    },
  },
});

const sizeVariants = variant({
  prop: 'size',
  variants: {
    sm: {
      px: '8',
      height: 'small',
      fontSize: 'small',
      minWidth: '73px',
      minHeight: '46px',
    },
    lg: {
      px: '8',
      height: 'large',
      fontSize: 'default',
      minWidth: '88px',
      minHeight: '62px',
    },
  },
});

const commonStyles = () => css`
  box-sizing: border-box;
  position: relative;
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  appearance: none;
  cursor: pointer;
  outline: none;
  transition: all 300ms ease 0s;
  color: ${props => props.theme.colors.text};
  background-color: #ffffff;
`;

const StyledSelectionBlock = styled(Flex)`
  ${commonStyles}
  ${typeVariants}
  ${sizeVariants}
`;
